import { Button, ButtonText } from "../../../ui/button";
import { HStack } from "../../../ui/hstack";
import { Text } from "../../../ui/text";
import { VStack } from "../../../ui/vstack";
import { WorkOrderPaymentMethodEnum } from "@repo/graphql/gql/graphql";
import React from "react";

export type PaymentMethodOption = {
  method: WorkOrderPaymentMethodEnum;
  available: boolean;
  unavailableReason?: string | null;
};

type Props = {
  options: PaymentMethodOption[];
  value?: WorkOrderPaymentMethodEnum;
  onChange: (method: WorkOrderPaymentMethodEnum) => void;
  labels: Partial<Record<WorkOrderPaymentMethodEnum, string>>;
};

/** Shared between the work-order brand checkout and the affiliate payout
 * checkout — both offer the same `WorkOrderPaymentMethodEnum` options, each
 * with its own server-computed `available`/`unavailableReason`. `labels`
 * stays a prop instead of a hardcoded record because each checkout only
 * ever offers a subset of the enum. */
const PaymentMethodSelector = React.memo(
  ({ options, value, onChange, labels }: Props) => {
    const unavailableReasons = options.filter(
      (option) => !option.available && option.unavailableReason,
    );

    return (
      <VStack space="xs">
        <HStack flexSpace="xs">
          {options.map((option) => {
            const isSelected = option.method === value;
            return (
              <Button
                key={option.method}
                size="sm"
                rounded="lg"
                className="flex-1"
                action={isSelected ? "brand" : "muted"}
                variant={isSelected ? "solid" : "outline"}
                isDisabled={!option.available}
                onPress={() => onChange(option.method)}
              >
                <ButtonText>
                  {labels[option.method] ?? option.method}
                </ButtonText>
              </Button>
            );
          })}
        </HStack>
        {unavailableReasons.map((option) => (
          <Text key={option.method} size="xs" action="muted">
            {option.unavailableReason}
          </Text>
        ))}
      </VStack>
    );
  },
);

PaymentMethodSelector.displayName = "PaymentMethodSelector";

export default PaymentMethodSelector;
